import { useEffect } from 'react';
import { create } from 'zustand';
import useStore from 'Hooks/useStore';
import { fetchUserById } from 'Services/Api/_HumanWritten/UserService';
import { stringNotEmpty } from 'Util/TypeGuards';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';

function extractDisplayNameId(user: any): string | null {
	return user?.userDisplayNameId ?? user?.userDisplayName?.id ?? null;
}

interface UserDisplayNameLookupState {
	/**
	 * Resolved UserDisplayName ids keyed by userId. Null = the user has no
	 * display name (or the fetch failed). Kept in a zustand store (rather than a
	 * plain map) so consumers — including the wizard tab definitions, whose
	 * visibility depends on the subject's customer type — re-render when a
	 * lookup resolves.
	 */
	byUserId: Record<string, string | null>;
}

export const useUserDisplayNameLookupStore = create<UserDisplayNameLookupState>(() => ({
	byUserId: {},
}));

const pendingLookups = new Set<string>();

/**
 * Fire-and-forget resolution of a user's UserDisplayName id. No-ops when the
 * lookup is already resolved or in flight. Failures are cached as null so a
 * failing endpoint isn't hammered.
 */
export function resolveUserDisplayNameId(userId: string): void {
	if (!stringNotEmpty(userId)) return;
	if (userId in useUserDisplayNameLookupStore.getState().byUserId) return;
	if (pendingLookups.has(userId)) return;
	pendingLookups.add(userId);
	fetchUserById(userId)
		.then(user => {
			useUserDisplayNameLookupStore.setState(s => ({
				byUserId: { ...s.byUserId, [userId]: extractDisplayNameId(user) },
			}));
		})
		.catch(() => {
			useUserDisplayNameLookupStore.setState(s => ({
				byUserId: { ...s.byUserId, [userId]: null },
			}));
		})
		.finally(() => {
			pendingLookups.delete(userId);
		});
}

/**
 * Returns the booking subject's UserDisplayName id, for filtering by customer type.
 *
 * The subject is the wizard's selected customer (`wizardData.userId`) — which
 * covers staff booking on behalf — falling back to the logged-in user. Callers
 * outside the wizard (e.g. the check-in booking form) can pass the subject's
 * userId explicitly instead. The logged-in user's own display name comes from
 * the auth payload; other subjects are fetched once and cached in the lookup
 * store, re-rendering subscribers when resolved.
 *
 * **`undefined` means "still resolving", `null` means "resolved: no customer type".** The two used
 * to be collapsed into `null`, which was harmless while this only hid a dynamic booking field but
 * is not any more: the measurement filter treats `null` as load-bearing (an `Only` list cannot be
 * matched by an absent value, so the measurement is hidden), and the reset hooks act on that by
 * writing a replacement length into the booking. A staff member opening a contractor's booking
 * would have had its contractor-only band swapped for a general one, priced, and never restored
 * when the fetch landed — the wrong fare decided by a network race, which is the exact failure the
 * feature exists to prevent. `undefined` is skipped by the filter, so nothing is hidden or moved
 * until the answer is actually known.
 */
export function useSubjectUserDisplayNameId(explicitSubjectUserId?: string): string | null | undefined {
	const store = useStore();
	const { wizardData } = useBookingWizardDataStore();
	const byUserId = useUserDisplayNameLookupStore(s => s.byUserId);

	const wizardSubjectUserId = stringNotEmpty(wizardData?.userId) ? wizardData.userId : store.userId;
	// An explicitly supplied subject (even an empty one, i.e. "no customer chosen
	// yet") takes precedence over the wizard's state.
	const subjectUserId = (explicitSubjectUserId !== undefined ? explicitSubjectUserId : wizardSubjectUserId) ?? '';

	const isSelf = stringNotEmpty(subjectUserId) && subjectUserId === store.userId;
	const ownId = extractDisplayNameId((store as any)?.userData);

	useEffect(() => {
		if (!stringNotEmpty(subjectUserId)) return;
		if (isSelf && ownId) return;
		resolveUserDisplayNameId(subjectUserId);
	}, [subjectUserId, isSelf, ownId]);

	if (isSelf && ownId) {
		return ownId;
	}
	if (!stringNotEmpty(subjectUserId)) {
		// No customer chosen. That is an answer, not a pending one.
		return null;
	}
	// Presence of the key is what distinguishes "resolved to nothing" from "not back yet" — a
	// resolved lookup stores null, so `?? null` would erase the difference.
	return subjectUserId in byUserId ? byUserId[subjectUserId] : undefined;
}
